import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Chrome, Mail, Info, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../utils/supabase/client';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { LogoIcon } from '../assets';
import { cn } from '@/lib/utils';

export default function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string>('');

  // Clear any pending signup session data when user visits login page
  useEffect(() => {
    sessionStorage.removeItem('signupStep1Data');
    sessionStorage.removeItem('signupData');

      // Autofill email from URL if present
  const searchParams = new URLSearchParams(window.location.search);
  const emailParam = searchParams.get('email');
  if (emailParam) {
    setFormData(prev => ({ ...prev, email: decodeURIComponent(emailParam) }));
  }

    // Check for OAuth callback and existing session
    const checkSession = async () => {
      try {
        // Check if this is an OAuth callback (has hash or code in URL)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        if (hashParams.get('access_token') || searchParams.get('code')) {
          setIsLoading(true);

          // Get the session from Supabase (it will handle the OAuth callback)
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            setAuthError('Authentication failed. Please try again.');
            setIsLoading(false);
            // Clean up URL
            window.history.replaceState({}, document.title, '/login');
            return;
          }

          if (session?.access_token) {
            localStorage.setItem('accessToken', session.access_token);
            toast.success('Successfully logged in with Google!');
            navigate('/dashboard?cache=false');
            return;
          }
        }

        // Check if we have an existing valid session
        const { data: { session }, error } = await supabase.auth.getSession();

        if (session?.access_token) {
          // User is already logged in, redirect to dashboard
          localStorage.setItem('accessToken', session.access_token);
          navigate('/dashboard?cache=false');
          return;
        }

        if (error) {
          await supabase.auth.signOut();
          localStorage.removeItem('accessToken');
        }
      } catch (error) {
        // Clear tokens just in case
        await supabase.auth.signOut();
        localStorage.removeItem('accessToken');
      }
    };

    checkSession();
  }, [navigate]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    // Clear previous auth error
    setAuthError('');

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    // Mark all fields as touched when submitting
    setTouched({ email: true, password: true });

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          // Show inline error instead of toast
          setAuthError(error.message || 'Failed to login. Please check your credentials and try again.');
          setIsLoading(false);
          return;
        }

        // Store access token
        if (data.session?.access_token) {
          localStorage.setItem('accessToken', data.session.access_token);
        }

        // Check if email is verified (for informational toast only, doesn't block login)
        const isOAuthProvider = data.user?.app_metadata?.provider !== 'email';
        const isEmailVerified = !!data.user?.email_confirmed_at;

        if (!isOAuthProvider && !isEmailVerified) {
          // Email not verified - show info toast but allow access
          toast.info('Email verification pending', {
            description: 'Check your inbox to verify your email address',
            duration: 4000,
          });
        } else {
          toast.success('Login successful!');
        }

        navigate('/dashboard');
      } catch (error) {
        setAuthError('Failed to login. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Check if we're in an iframe (Figma preview)
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Check if in iframe
      if (isInIframe()) {
        setAuthError(
          'Google Sign-In cannot work in Figma preview due to Google\'s security restrictions. ' +
          'Please test this feature by deploying to Netlify or running locally (npm run dev). ' +
          'For now, use email/password login to test in Figma.'
        );
        toast.error('Google OAuth blocked in iframe', {
          description: 'Deploy to Netlify or run locally to test Google Sign-In',
          duration: 6000,
        });
        return;
      }

      setIsLoading(true);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/oauth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        setAuthError(error.message || 'Failed to login with Google. Please try again.');
        setIsLoading(false);
        return;
      }

      // The user will be redirected to Google, so we don't need to do anything else here
      toast.info('Redirecting to Google...');
    } catch (error) {
      setAuthError('Failed to login with Google. Please try again.');
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    // Microsoft OAuth - Coming Soon
    toast.info('Microsoft Sign-In', {
      description: 'Microsoft authentication will be implemented soon. For now, please use Google or email/password.',
      duration: 5000,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md space-y-4">

        <Card>
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="mx-auto  flex h-12 w-12 items-center justify-center rounded-lg">
                <LogoIcon className="w-12 h-12" />
              </div>
            </div>
            <CardTitle className="text-center">Welcome to AxOrigin</CardTitle>
            <CardDescription className="text-center">
              Sign in to access your accessibility platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            {authError && (
              <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{authError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    // Clear auth error when user starts typing
                    setAuthError('');
                    // Clear field error if value is valid
                    if (e.target.value && validateEmail(e.target.value)) {
                      setErrors({ ...errors, email: '' });
                    }
                  }}
                  onBlur={() => setTouched({ ...touched, email: true })}
                  className={cn(
                      errors.email && touched.email
                        ? "border-destructive"  // Error state (red, visible)
                        : "border border-neutral-300 dark:border-neutral-600"  // Explicit neutral grays for visibility
                    )}
                  aria-invalid={!!(errors.email && touched.email)}
                  aria-describedby={errors.email && touched.email ? 'email-error' : undefined}
                />
                {errors.email && touched.email && (
                  <p id="email-error" className="text-xs text-destructive" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      // Clear auth error when user starts typing
                      setAuthError('');
                      // Clear field error if value is valid
                      if (e.target.value.length >= 8) {
                        setErrors({ ...errors, password: '' });
                      }
                    }}
                    onBlur={() => setTouched({ ...touched, password: true })}
                    className={cn(
                      "pr-10",  // Keep padding
                      errors.password && touched.password
                        ? "border-destructive"  // Error state (red, visible)
                        : "border border-neutral-300 dark:border-neutral-600"  // Explicit neutral grays for visibility
                    )}
                    aria-invalid={!!(errors.password && touched.password)}
                    aria-describedby={errors.password && touched.password ? 'password-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && touched.password && (
                  <p id="password-error" className="text-xs text-destructive" role="alert">
                    {errors.password}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full bg-primary-gradient cursor-pointer" disabled={isLoading}>
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>

              <div className="text-center">
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
            </form>

            {/*  <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                Or continue with
              </span>
            </div> */}

            <div className="grid gap-3">
              {/* <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full"
            >
              <Chrome className="mr-2 h-4 w-4" />
              {isLoading ? 'Redirecting...' : 'Login with Google'}
            </Button>
            */}
              {/* <Button
              type="button"
              variant="outline"
              onClick={handleMicrosoftLogin}
              className="w-full opacity-60"
              title="Coming soon"
            >
              <Mail className="mr-2 h-4 w-4" />
              Login with Microsoft
            </Button> */}
            </div>

            {/* {isInIframe() && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <p className="flex items-center gap-2">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> Google/Microsoft Sign-In won't work in Figma preview.
                    Deploy to <a href="/DEPLOY_TO_NETLIFY.md" className="underline font-medium">Netlify</a> or run locally to test.
                  </span>
                </p>
              </div>
            )} */}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-sm text-center text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup/step1" className="text-primary hover:underline">
                Sign up here
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
